/**
 * 资产分类法（前端侧）。
 *
 * 权威来源是后端 `GET /api/asset-library/taxonomy`（后端 `app/taxonomy.py`），
 * 这里的副本只在两种情况下用：
 *   1. 接口还没回来的首屏（避免分类 chip 闪一下空白）
 *   2. `VITE_API_MODE=mock` 离线模式
 *
 * `classifyLocal` 的规则必须与后端 `taxonomy.classify` 逐条对齐 ——
 * 两边不一致的症状是「离线看到模特图、连上后端变成产品图」。
 */

export type AssetCategoryId =
  | 'model'
  | 'product'
  | 'scene'
  | 'white-bg'
  | 'detail'
  | 'video'
  | 'tool'
  | 'social'
  | 'copy'
  | 'other'

export type AssetOriginId = 'generated' | 'upload'

export type AssetCategory = {
  id: AssetCategoryId
  label: string
  icon: string
  desc: string
  order: number
}

export type AssetOrigin = { id: AssetOriginId; label: string; desc: string }

/** 与后端 CATEGORIES 同序 —— 顺序即 chip 排列顺序 */
export const FALLBACK_CATEGORIES: AssetCategory[] = [
  { id: 'model', label: '模特图', icon: 'users', desc: 'AI 模特 / 服装上身 / 换脸', order: 1 },
  { id: 'product', label: '产品图', icon: 'box', desc: '多角度 / 细节 / 卖点 / 包装 / 套图', order: 2 },
  { id: 'scene', label: '场景图', icon: 'image', desc: '商品置入环境 / 换背景 / Banner / 展台', order: 3 },
  { id: 'white-bg', label: '白底图', icon: 'layers', desc: '平铺图 / 挂拍 / 去背景 / 立体图', order: 4 },
  // ⚠️ 这个分类的 `desc` 不再写「详情页长图」：那个形态 2026-09-30 下架
  // （「详情的长图不搞先」）。但下面 `BY_SLUG` 里的 `detail-long` **必须留着** ——
  // 库里还有它的历史产物，删了这一行它们会掉进「未分类」。
  // 描述文案与分类键是两件事：前者跟着「现在能做什么」，后者跟着「数据里有什么」。
  { id: 'detail', label: '详情页', icon: 'grid', desc: '主图套图 / 平台详情模块 / 模仿版式', order: 5 },
  { id: 'video', label: '视频', icon: 'video', desc: '主图视频 / 带货短视频 / 视频复刻 / 数字人', order: 6 },
  { id: 'tool', label: '处理图', icon: 'wand', desc: '放大 / 精修 / 扩图 / 水印 / 跨平台派生', order: 7 },
  { id: 'social', label: '社媒图', icon: 'star', desc: '小红书种草 / 朋友圈配图 / Instagram · FB', order: 8 },
  { id: 'copy', label: '文案', icon: 'text', desc: '卖点 / 详情页文案 / 笔记 / 标题', order: 9 },
  { id: 'other', label: '未分类', icon: 'folder', desc: '无法从来源推导', order: 99 },
]

export const FALLBACK_ORIGINS: AssetOrigin[] = [
  { id: 'generated', label: 'AI 生成', desc: '本工作台产出的成品' },
  { id: 'upload', label: '我上传的', desc: '上传的参考图 / 白底素材' },
]

const CATEGORY_MAP = new Map(FALLBACK_CATEGORIES.map((c) => [c.id, c]))

export function categoryById(id?: string): AssetCategory {
  return CATEGORY_MAP.get(id as AssetCategoryId) ?? CATEGORY_MAP.get('other')!
}

export function categoryLabel(id?: string): string {
  return categoryById(id).label
}

export function categoryIcon(id?: string): string {
  return categoryById(id).icon
}

export function isKnownCategory(id: string): boolean {
  return CATEGORY_MAP.has(id as AssetCategoryId)
}

/* -------------------------------------------------------------------------- */
/* 离线兜底分类（与后端 taxonomy.classify 同规则）                              */
/* -------------------------------------------------------------------------- */

const BY_SLUG: Record<string, AssetCategoryId> = {
  'ai-set': 'product',
  banner: 'scene',
  copywriting: 'copy',
  'detail-main': 'detail',
  'detail-long': 'detail',
  'detail-aplus': 'detail',
  'detail-copy': 'detail',
  'social-xhs': 'social',
  'social-moments': 'social',
  'social-overseas': 'social',
  'main-video': 'video',
  'short-video': 'video',
  'video-replicate': 'video',
  // 九宫格分镜参考图（2026-09-29）：视频页第 3 步先出的那张图。
  // 产出是图片，但归属跟着视频那条流水线走 —— 与后端 taxonomy._BY_SLUG 同口径，
  // 两边不一致的症状是「离线看到视频、连上后端变成未分类」。
  storyboard: 'video',
  'digital-talking-head': 'video',
  'digital-clone-avatar': 'video',
}

const BY_FEATURE: Record<string, AssetCategoryId> = {
  'clothing-on': 'model',
  // 「专业服饰 · 模特生成」那一档 2026-10-10 下架了（与全品类 AI 模特图重复），
  // 但这条**留着** —— 库里还有以 `fashion-model-gen` 落库的历史产出，删了会掉「未分类」。
  // 与下面 `main-video` / `short-video`（并档后仍保留）是同一条规矩：留着是给数据的，不是给功能的。
  'model-gen': 'model',
  'face-swap': 'model',
  'product-main': 'product',
  'product-model': 'model',
  'product-selling-point': 'product',
  'product-multi-angle': 'product',
  'product-detail': 'product',
  'product-packaging': 'product',
  'product-render': 'product',
  variations: 'product',
  'garment-3d': 'product',
  // 服饰细节与全品类「细节特写」同属「讲做工」的图，归产品图
  'garment-detail': 'product',
  'product-scene': 'scene',
  // 展台背景是「商品置入环境」，归场景图
  'product-booth': 'scene',
  'bg-swap': 'scene',
  'garment-flat-lay': 'white-bg',
  // 挂拍与平铺同属白底展示，只是有无衣架
  'garment-hang': 'white-bg',
  'remove-bg': 'white-bg',
}

/**
 * source_page 兜底表 —— **只服务两件事**，别当成「当前页面清单」删掉：
 *   1. 上传参考图时 `sourcePageFromLocation()` 推出来的值是**页面路径**
 *      （`social-image` / `localize` / `digital-human`），不是 app_slug；
 *   2. 老任务的历史数据里 source_page 也是这套写法。
 * 提交任务时写的 source_page 已改成与 `jobsFor()` 同键的 app_slug
 * （`social-xhs` / `localize-platform` / `digital-talking-head`），
 * 那些由上面的 BY_SLUG 精确表接住 —— 两张表管的是不同的调用方。
 */
const BY_SOURCE_PAGE: Record<string, AssetCategoryId> = {
  banner: 'scene',
  'ai-set': 'product',
  copywriting: 'copy',
  'img-localize': 'tool',
  'localize': 'tool',
  'social-image': 'social',
  'digital-human': 'video',
  'detail-page': 'detail',
  // 带货短视频 + 商品视频于 2026-09-29 合并为 /video-studio（页面键先统一），
  // 2026-10-05 两档**连档位也并成了一档**（页面顶部那排 chip 撤了）。
  // ⚠️ 后两条（`main-video` / `short-video`）**不能删** —— 并档删的是档位，
  // 不是历史数据：库里旧任务的 `source_page` 与 `app_slug` 里仍写着它们，
  // 删掉这条映射，那批记录会掉进「未分类」。
  'video-studio': 'video',
  'main-video': 'video',
  'short-video': 'video',
  'video-replicate': 'video',
  'professional/fashion': 'model',
  'professional/products': 'product',
}

/**
 * 整段前缀 → 类别。
 *
 * `tool-`：工具箱里对单张图做处理（去背景 / 放大 / 精修）。
 * `localize-`：跨平台派生。它同样是「加工已有成片」，不是「从白底图生成」，
 *   所以与工具箱同归「处理图」；派生目标（哪个平台 / 哪种语言）不影响类别。
 * 前缀必须先于 feature 后缀判定 —— 否则 `tool-remove-bg` 会被
 * `remove-bg → white-bg` 抢走，而它是「工具改图」不是「做白底图」。
 */
const PREFIX_CATEGORY: [string, AssetCategoryId][] = [
  ['tool-', 'tool'],
  ['localize-', 'tool'],
]

/** 与后端同优先序：kind → slug 精确表 → 前缀表 → feature 后缀 → source_page */
export function classifyLocal(
  appSlug?: string | null,
  kind: string = 'image',
  sourcePage?: string | null,
): AssetCategoryId {
  if (kind === 'text') return 'copy'
  if (kind === 'video') return 'video'

  const slug = (appSlug ?? '').trim().toLowerCase()
  if (slug) {
    if (BY_SLUG[slug]) return BY_SLUG[slug]
    for (const [prefix, category] of PREFIX_CATEGORY) {
      if (slug.startsWith(prefix)) return category
    }
    for (const [feature, category] of Object.entries(BY_FEATURE)) {
      if (slug === feature || slug.endsWith(`-${feature}`)) return category
    }
  }

  const page = (sourcePage ?? '').trim().toLowerCase()
  if (page) {
    if (BY_SOURCE_PAGE[page]) return BY_SOURCE_PAGE[page]
    for (const [key, category] of Object.entries(BY_SOURCE_PAGE)) {
      if (page.endsWith(key)) return category
    }
  }

  return 'other'
}
